import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AnimeCard from '@/components/base/AnimeCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { animeList, siteInfo } from '@/mocks/anime';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const anime = animeList.find((a) => a.slug === slug);

  if (!anime) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-10 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-800 text-foreground-500">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-100 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-500 text-sm mb-6">
              您访问的内容不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedAnime = animeList
    .filter((a) => a.categorySlug === anime.categorySlug && a.id !== anime.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: anime.category, to: `/category/${anime.categorySlug}` },
    { label: anime.title },
  ];

  return (
    <>
      <Navbar />

      <main className="pt-20 pb-10">
        <article className="max-w-[1000px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Cover Image */}
          <div className="relative w-full aspect-[21/9] rounded-lg overflow-hidden mb-8 bg-background-900">
            <img
              src={anime.coverImage}
              alt={anime.title}
              title={`${anime.title} - 羞羞动漫高清番剧详情`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent"></div>
          </div>

          {/* Title & Meta */}
          <header className="mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 mb-4">
              {anime.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-500/10 text-primary-500">
                <i className="ri-star-fill"></i>
                <span className="font-semibold">{anime.rating}</span>
              </div>
              <div className="flex items-center gap-2 text-foreground-400">
                <i className="ri-film-line text-base"></i>
                <span>共{anime.episodes}集</span>
              </div>
              <div className="flex items-center gap-2 text-foreground-400">
                <i className="ri-calendar-line text-base"></i>
                <span>{anime.releaseYear}年</span>
              </div>
              <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                anime.status === '连载中'
                  ? 'bg-accent-500/10 text-accent-500'
                  : 'bg-background-800 text-foreground-400'
              }`}>
                {anime.status}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {anime.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-background-800 text-foreground-400 border border-background-700/50"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          {/* Content */}
          <section className="mb-10">
            <h2 className="text-lg font-heading font-semibold text-foreground-100 mb-4">
              剧情简介
            </h2>
            <div
              className="prose-custom text-foreground-300 text-sm leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: anime.content }}
            />
          </section>

          {/* Update Date */}
          <div className="flex items-center gap-2 text-sm text-foreground-600 mb-10 pb-8 border-b border-background-800/50">
            <i className="ri-time-line"></i>
            <span>最后更新于 {anime.updateDate}</span>
          </div>

          {/* Related Recommendations */}
          {relatedAnime.length > 0 && (
            <section>
              <h2 className="text-lg font-heading font-semibold text-foreground-100 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedAnime.map((item) => (
                  <AnimeCard key={item.id} anime={item} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: anime.title,
            description: anime.description,
            image: anime.coverImage,
            datePublished: `${anime.releaseYear}-01-01`,
            dateModified: anime.updateDate,
            author: {
              '@type': 'Organization',
              name: siteInfo.name,
            },
            publisher: {
              '@type': 'Organization',
              name: siteInfo.name,
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://xixiudongman.com/article/${anime.slug}`,
            },
          }),
        }}
      />
    </>
  );
}